import * as Sentry from "@sentry/react";
import { identifyPostHogUser, resetPostHog, identifySentryUser, resetSentryUser } from "@alascorporation/observability";
import { posthog } from "../observability/posthog";
import { getToken, getStoredUser, persistAuth, clearAuth } from "@alascorporation/hooks";
import { hasPermission, isStaffProfile, type PermissionKey, type StaffProfile } from "@alascorporation/access-control";

type StoredUser = {
  id?: string;
  name?: string;
  email?: string;
  role?: string;
  mustChangePassword?: boolean;
  onboardedAt?: string | null;
  // gestion-custom-roles-permissions: resolved server-side at login/session-
  // restore/me time (from the user's database Role, or the legacy fallback
  // if none is assigned) — see resolvePermissionKeys in platforms/api. Only
  // as fresh as the last login/reload; the backend re-resolves per request
  // regardless, so this is purely a UI-gating convenience, never the source
  // of truth for authorization.
  permissionKeys?: PermissionKey[];
};

export { getToken, isStaffProfile };
export type { PermissionKey, StaffProfile };

export function getCurrentUser(): StoredUser | null {
  return getStoredUser<StoredUser>();
}

/**
 * Any of the three staff profiles (Administrador, Docente, Gestor CCCD) —
 * not just `admin`. Renamed from the old `isAdminAuthenticated`, which
 * literally only accepted `role === "admin"` and is why a Docente couldn't
 * log into gestion before RBAC.
 */
export function isStaffAuthenticated(): boolean {
  const token = getToken();
  const user = getCurrentUser();
  return Boolean(token && user?.role && isStaffProfile(user.role));
}

export function getCurrentProfile(): StaffProfile | null {
  const user = getCurrentUser();
  return user?.role && isStaffProfile(user.role) ? user.role : null;
}

/**
 * True if the current user is authorized for every given permission key.
 * `admin` always passes. Reads the server-resolved `permissionKeys` from
 * the stored user (works for both a database-backed custom role and the
 * legacy fallback — the backend resolves either into the same array), so
 * this stays correct for custom roles without the frontend needing its own
 * copy of the legacy expansion table.
 */
export function hasCurrentPolicy(...keys: PermissionKey[]): boolean {
  const profile = getCurrentProfile();
  if (!profile) return false;
  const granted = getCurrentUser()?.permissionKeys ?? [];
  return keys.every((key) => hasPermission(profile, granted, key));
}

export function mustChangePassword(): boolean {
  return Boolean(getCurrentUser()?.mustChangePassword);
}

/**
 * Sets the gate flag on the persisted user — used by `api.ts`'s
 * `onMustChangePassword` callback to catch a mid-session gate (e.g. an admin
 * resets this account's password while it's logged in elsewhere), and by
 * `ChangePasswordPage.tsx` to clear it on success. See
 * openspec/changes/enforce-must-change-password-gate design.md Decision 5.
 */
export function setMustChangePassword(value: boolean): void {
  const token = getToken();
  const user = getCurrentUser();
  if (token && user) {
    persistAuthSession(token, { ...user, mustChangePassword: value });
  }
}

/** True the first time this account logs in — before the welcome tutorial has run or been dismissed. */
export function needsOnboarding(): boolean {
  return !getCurrentUser()?.onboardedAt;
}

export function persistAuthSession(token: string, user: StoredUser): void {
  persistAuth(token, user);
  if (user.id) {
    identifySentryUser(Sentry, { id: user.id, email: user.email, username: user.name, role: user.role });
    identifyPostHogUser(posthog, { id: user.id, email: user.email, role: user.role });
  }
}

export function clearAuthSession(): void {
  clearAuth();
  resetSentryUser(Sentry);
  resetPostHog(posthog);
}

export function initSentryUser(): void {
  const user = getCurrentUser();
  if (user?.id) {
    identifySentryUser(Sentry, { id: user.id, email: user.email, username: user.name, role: user.role });
    identifyPostHogUser(posthog, { id: user.id, email: user.email, role: user.role });
  }
}
